import type { ReactNode } from "react";
import { Button } from "@ascleo/ui/components/ui/button";
import { FieldError } from "@ascleo/ui/components/ui/field";
import { CircleCheck, Circle, type LucideIcon } from "lucide-react";
import { cn } from "@ascleo/ui/lib/utils";

/**
 * One option of a {@link CheckInChoiceGroup}.
 *
 * @since 1.3.0
 * @example
 * ```ts
 * const option: CheckInChoiceOption<"M" | "W"> = { value: "W", label: "Weiblich", testId: "gender-w" };
 * ```
 */
export interface CheckInChoiceOption<T extends string> {
  readonly value: T;
  readonly label: string;
  readonly testId: string;
  /** Optional intent icon; the selection indicator is always shown in front of it. */
  readonly icon?: LucideIcon;
}

/**
 * Touch-sized single choice for 2–4 options (gender, insurance type): one
 * 56px button per option with `aria-pressed`, a constant size and one selected
 * style, so the selected option neither grows nor sticks out of the column
 * (KC-78, FC-08). Nothing is pre-selected unless `value` says so.
 *
 * @since 1.3.0
 * @example
 * ```tsx
 * <CheckInChoiceGroup
 *   legend={t("self-service.check-in.register-basics.gender")}
 *   options={genderOptions}
 *   value={field.state.value}
 *   onChange={field.handleChange}
 * />
 * ```
 */
export function CheckInChoiceGroup<T extends string>({
  legend,
  options,
  value,
  onChange,
  error,
  errorTestId,
  disabled,
}: {
  readonly legend: ReactNode;
  readonly options: readonly CheckInChoiceOption<T>[];
  /** Selected value, or `""` while nothing is chosen. */
  readonly value: T | "";
  readonly onChange: (value: T) => void;
  readonly error?: ReactNode;
  readonly errorTestId?: string;
  readonly disabled?: boolean;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-2 border-0 p-0">
      <legend className="mb-2 p-0 text-lg font-medium text-foreground">
        {legend}
      </legend>
      <div className="@container min-w-0">
      <div className="grid min-w-0 grid-cols-2 gap-3 @2xl:grid-cols-4">
        {options.map((option) => {
          const selected = option.value === value;
          const Indicator = selected ? CircleCheck : Circle;
          const Icon = option.icon;
          return (
            <Button
              key={option.value}
              type="button"
              size="2xl"
              variant="outline"
              aria-pressed={selected}
              disabled={disabled}
              data-testid={option.testId}
              onClick={() => onChange(option.value)}
              className={cn(
                "min-w-0 justify-start border-2 px-4 text-lg",
                selected &&
                  "border-primary bg-primary-soft text-primary-ink hover:bg-primary-soft",
              )}
            >
              <Indicator aria-hidden className="size-6 shrink-0" />
              {Icon ? <Icon aria-hidden className="size-6 shrink-0" /> : null}
              <span className="min-w-0 truncate">{option.label}</span>
            </Button>
          );
        })}
      </div>
      </div>
      {error ? <FieldError data-testid={errorTestId}>{error}</FieldError> : null}
    </fieldset>
  );
}
